<template>
  <div v-loading="loading" class="wrapper" ref="doneListRoot">
    <!-- 多语言切换按钮（已办列表页：不绑定流程标识，强制走用户灰度策略） -->
    <LanguageSwitcher
      :rootEl="translateRoot"
      :force-user-gray="true"
      :tz-enabled="isTimezoneEnabled"
      :tz-mode="tzCurrentMode"
      :tz-timezone="tzSystemTimezone"
      @tz-switch-local="tzSwitchToLocal"
      @tz-switch-beijing="tzSwitchToBeijing"
    />
    <!-- 语音助手组件 -->
    <VoiceAssistant 
      v-if="showVoice"
      ref="voiceAssistant"
      :auto-call-ai="true"
      :initial-position="{ x: null, y: 120 }"
      @voice-recognition-complete="handleVoiceRecognition"
      @ai-response="handleAIResponse"
      @ticket-response="handleTicketResponse"
      @ai-error="handleAIError"
    />
    <div class="todo-box">
      <div class="content" :style="{ height: height + 'px' }">
        <!-- 左侧分类面板 -->
        <div class="nav" :class="[{ 'is-fold': isFold }]">
          <div class="nav-header">
            <span class="nav-header-title">分类</span>
          </div>
          <div class="nav-list">
            <div
              v-for="(item, index) in titleLists"
              :key="item.name"
              :class="['nav-item', { 'nav-item--active': sidebarNum === index }]"
              @click="sidebarNum = index; getTicketByType(index)"
            >
              <span class="nav-item__text">{{ item.name }}</span>
              <span class="nav-item__badge" v-if="item.count > 0">{{ item.count > 99 ? '99+' : item.count }}</span>
            </div>
          </div>
        </div>

        <!-- 右侧主内容区 -->
        <div :class="['list-box', { 'is-stretch': isFold }]">
          <!-- 顶部标题栏 -->
          <div :class="['todo-header', { 'is-stretch': isFold }]">
            <div class="todo-header__left" @click="isFold = !isFold">
              <van-icon :name="isFold ? 'wap-nav' : 'arrow-left'" class="toggle-icon" />
            </div>
            <div class="todo-header__center">
              <span class="todo-header__title">{{ currentTypeName }}</span>
              <span class="todo-header__count">{{ total || 0 }}</span>
            </div>
            <div class="todo-header__right">
              <div class="search-toggle" @click="hasSearch ? hasSearch = false : onCancel()">
                <van-icon :name="hasSearch ? 'search' : 'cross'" size="18" />
              </div>
            </div>
          </div>

          <!-- 搜索栏 -->
          <transition name="slide-down" appear>
            <div
              key="search"
              v-show="!hasSearch"
              :class="['search-panel', { 'is-stretch': isFold }]"
            >
              <!-- 搜索输入框（scanner自带输入框+扫码） -->
              <div class="search-panel__input">
                <scanner @enter="checkSearch" @cancel="onCancel" :searchValue="searchValue" :placeholder="'搜索工单号、标题或关键词'"></scanner>
                <div class="search-panel__cancel" @click="onCancel">取消</div>
              </div>
              <!-- 辅助输入工具栏：语音 + OCR -->
              <div v-if="showVoice || showOcr" class="search-toolbar">
                <!-- 语音输入 -->
                <div
                  v-if="showVoice && !isVoiceModalOpen"
                  class="toolbar-btn"
                  @click="openVoiceSearch"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
                    <rect x="9" y="4" width="6" height="10" rx="3" stroke="#0052d9" stroke-width="2" fill="none"/>
                    <path d="M6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12" stroke="#0052d9" stroke-width="2" stroke-linecap="round"/>
                    <line x1="12" y1="18" x2="12" y2="21" stroke="#0052d9" stroke-width="2" stroke-linecap="round"/>
                    <line x1="9" y1="21" x2="15" y2="21" stroke="#0052d9" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>语音输入</span>
                </div>
                <!-- OCR图片识别 -->
                <ImageUploadInput
                  v-if="showOcr"
                  field-name="doneSearch"
                  field-title="图片识别"
                  :new-choose-image="true"
                  :show-toolbar-style="true"
                  @ocr-text-selected="handleOcrTextSelected"
                />
              </div>
            </div>
          </transition>

          <!-- 已办列表 -->
          <van-pull-refresh v-model="refreshing" @refresh="onRefresh" class="todo-list">
            <van-list
              v-model="loading"
              :finished="finished"
              finished-text="没有更多了"
              @load="onLoad"
              offset="10"
              :class="[
                hasSearch ? 'hide-search-list' : 'show-search-list',
                (showVoice || showOcr) ? 'with-toolbar' : ''
              ]"
            >
              <!-- 空状态 -->
              <div v-if="!loading && ticketList.length === 0" class="empty-state">
                <van-icon name="notes-o" size="48" color="#c0c4cc" />
                <p>暂无已办事项</p>
              </div>

              <div class="ticket-cards">
                <div
                  v-for="item in ticketList"
                  :key="item.TaskId || item.TaskOrderId"
                  class="ticket-card"
                  @click="toggleCardExpand(item.TaskId || item.TaskOrderId)"
                >
                  <!-- 卡片标题行 + 展开箭头 -->
                  <div class="ticket-card__title-row">
                    <div class="ticket-card__title">
                      {{ item.TodoName || item.TitleContent }}
                    </div>
                    <van-icon
                      :name="expandedCards.includes(item.TaskId || item.TaskOrderId) ? 'arrow-up' : 'arrow-down'"
                      class="expand-icon"
                      size="14"
                    />
                  </div>
                  <!-- 标签 + 去查看按钮 -->
                  <div class="ticket-card__header">
                    <div class="ticket-card__tags">
                      <span v-if="hasChinese(item.TypeName || item.Type)" class="type-tag">{{ item.TypeName || item.Type }}</span>
                    </div>
                    <van-button
                      v-if="!noPage.includes(item.Type)"
                      class="action-btn"
                      size="small"
                      round
                      @click.stop="gotoDetail(item)"
                    >去查看</van-button>
                  </div>
                  <!-- 卡片详情（展开） -->
                  <transition name="expand">
                    <div
                      v-show="expandedCards.includes(item.TaskId || item.TaskOrderId)"
                      class="ticket-card__body"
                      @click.stop
                    >
                      <div class="detail-grid">
                        <div v-for="desc in item.TodoDesc" :key="desc.key" class="detail-row">
                          <span class="detail-label">{{ desc.key }}</span>
                          <span class="detail-value" v-html="desc.value"></span>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </van-list>
          </van-pull-refresh>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { getOtobTicket, getAllType } from '@/api/tickets/ticketsApi';
import { mapGetters } from 'vuex';
import { Notify, Dialog } from 'vant';
import Scanner from './Scanner.vue';
import VoiceAssistant from '@/components/VoiceAssistant/todoAssist.vue';
import ImageUploadInput from '@/components/Formily/ImageUploadInput.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher/index.vue';
import timezoneMixin from '@/utils/timezoneMixin';
import { isTimezoneEnabled } from '@/utils/timezoneGate';
import '@/assets/iconfont/iconfont.css';
export default {
  mixins: [timezoneMixin],
  components: {
    Scanner,
    VoiceAssistant,
    ImageUploadInput,
    LanguageSwitcher,
  },
  computed: {
    ...mapGetters(['getConfigItem']),
    /** 时区转换灰度判定：列表页无流程标识 */
    isTimezoneEnabled() {
      return isTimezoneEnabled({
        getConfigItem: this.getConfigItem,
        skipProcessCheck: true,
      });
    },
    currentTypeName() {
      if (this.titleLists.length > 0 && this.sidebarNum < this.titleLists.length) {
        return this.titleLists[this.sidebarNum]?.name || '已办列表';
      }
      return '已办列表';
    },
  },
  data() {
    return {
      translateRoot: null, // 翻译作用范围（mounted 后设置）
      // 工单类型判断条件
      height: window.innerHeight,
      Type: '',
      TaskTypeId: '',
      TotalRows: '',
      //分页列表渲染
      List: [],
      ticketList: [],
      searchStr: this.$route.query?.search,
      total: 0,
      // 分页展示数据
      dataValueList: [],
      isFold: false,
      isLoading: false,
      activeName: '1',
      expandedCards: [],
      sidebarNum: parseInt(this.$route.query.num) || 0,
      loading: false,
      finished: false,
      hasSearch: !this.$route.query?.search,
      refreshing: false,
      // 分页控制
      pageSize: 30,
      currentPage: 1,
      // 数据类型展示
      titleLists: [],
      noPage: ['retire-block', 'retire-check', 'retire-ssd', 'retire-enter'],
      debounceFlag: false,
      searchValue: '',
      // 存储用户的完整语音输入（用于重新调用混元API）
      lastUserVoiceQuery: '',
      // 深度搜索控制
      isDeepSearching: false,
      deepSearchCancelled: false,
      // 深度搜索Toast实例
      deepSearchToast: null,
      showSearch:false,
      isShowSearch: false,
      // OCR识别组件是否显示
      showOcr: false,
      // 语音助手弹框是否打开
      isVoiceModalOpen: false,
      // 弹框状态检查定时器
      modalCheckTimer: null,
      isInGrayList: false,
      activeName: [],
    };
  },
  watch: {
    // 同步activeName到expandedCards，保证卡片默认展开
    activeName: {
      handler(newVal) {
        this.expandedCards = [...newVal];
      },
      immediate: true,
    },
  },
  async created() {
    window.addEventListener('resize', () => {
      // We execute the same script as before
      this.height = window.innerHeight;
    });
    await this.getTaskType();
    this.showSearch = this.getConfigItem('showSearch');
    if (sessionStorage.getItem('doneKey')) {
      // 如果有搜索关键词，执行搜索
      this.searchStr = sessionStorage.getItem('doneKey');
      this.searchValue = sessionStorage.getItem('doneKey');
      this.onSearch(this.searchStr);
    } else {
      await this.getTicketByType(this.sidebarNum || 0);
    }
  },
  mounted() {
    this.initUsers();
    // 翻译作用范围设为当前页面根元素
    this.translateRoot = this.$refs.doneListRoot || document.body;
  },
  beforeDestroy() {
    // 清理定时器
    if (this.modalCheckTimer) {
      clearInterval(this.modalCheckTimer);
      this.modalCheckTimer = null;
    }
  },
  methods: {
    hasChinese(text) {
      return text && /[\u4e00-\u9fa5]/.test(text);
    },
    toggleCardExpand(id) {
      const index = this.expandedCards.indexOf(id);
      if (index > -1) {
        this.expandedCards.splice(index, 1);
      } else {
        this.expandedCards.push(id);
      }
    },
    initUsers() {
      try {
        const users = JSON.parse(this.getConfigItem('showVoice'));
        const shouOcr = this.getConfigItem('shouOcr');
        
        if (users?.includes?.(sessionStorage.getItem('userid'))) {
          this.showVoice = true
        }
        
        // 读取OCR配置
        if (shouOcr === '1') {
          this.showOcr = true;
          console.log('[DonePage] OCR识别组件已启用');
        }
      } catch (e) {
        console.error('[DonePage] 初始化配置失败:', e);
      }
    },
    
    // 处理OCR识别的文本
    handleOcrTextSelected(text) {
      console.log('[DonePage] OCR识别文本:', text);
      
      if (!text || !text.trim()) {
        this.$toast({
          message: '未识别到有效文本',
          type: 'warning',
          duration: 2000
        });
        return;
      }
      
      // 将识别的文本填入搜索框
      this.searchValue = text.trim();
      this.searchStr = text.trim();
      
      // Aegis打点：OCR文本用于搜索
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          aegis.infoAll({
            msg: JSON.stringify({
              action: 'ocr_text_search',
              component: 'DonePage',
              searchText: text.trim(),
              timestamp: new Date().toISOString()
            }),
            ext1: 'ocr-search',
            trace: 'ocr-text-search',
          });
        }
      } catch (reportError) {
        console.error('[DonePage] Aegis上报失败:', reportError);
      }
      
      // 自动执行搜索
      this.checkSearch(text.trim());
      
      this.$toast({
        message: '已自动搜索识别的文字',
        type: 'success',
        duration: 2000
      });
    },
    
    // 打开语音搜索
    openVoiceSearch() {
      console.log('[DonePage] 打开语音搜索');
      
      if (!this.showVoice) {
        this.hasSearch = false;
        return;
      }
      
      // 检查语音助手组件是否存在
      if (!this.$refs.voiceAssistant) {
        console.error('[DonePage] 语音助手组件不存在');
        this.$toast({
          message: '语音助手未加载',
          type: 'fail',
          duration: 2000
        });
        return;
      }
      
      // 清除之前的定时器
      if (this.modalCheckTimer) {
        clearInterval(this.modalCheckTimer);
        this.modalCheckTimer = null;
      }
      
      // 设置弹框打开状态，隐藏语音图标
      this.isVoiceModalOpen = true;
      
      // 调用语音助手组件的方法打开弹窗
      this.$refs.voiceAssistant.showVoiceModal();
      
      // 监听弹框关闭事件（通过定时检查）
      this.modalCheckTimer = setInterval(() => {
        if (this.$refs.voiceAssistant && !this.$refs.voiceAssistant.voiceModalVisible) {
          this.isVoiceModalOpen = false;
          clearInterval(this.modalCheckTimer);
          this.modalCheckTimer = null;
          console.log('[DonePage] 语音助手弹框已关闭，恢复显示语音图标');
        }
      }, 500);
      
      // 等待弹窗打开后自动开始录音
      this.$nextTick(() => {
        setTimeout(() => {
          // 调用语音助手组件的开始录音方法
          this.$toast({
            message: '请说出关键词，例如：搜索尾号3456的工单',
            type: 'info',
            duration: 2000
          });
          if (this.$refs.voiceAssistant) {
            this.$refs.voiceAssistant.startClickRecording();
            console.log('[DonePage] 已自动开始录音');
          }
        }, 300); // 延迟300ms确保弹窗已完全打开
      });
    },
    
    checkSearch(val) {
      const isKeywordValid = val && val.length >= 2;
      if (isKeywordValid || !val) {
        this.onSearch(val);
      } else {
        Notify({ type: 'warning', message: '使用关键字搜索，请输入至少2个字符' });
      }
    },

    async onSearch(val, isLoadMore = false) {
      this.searchStr = val;
      sessionStorage.setItem('doneKey', val);
      
      // 如果不是加载更多，重置分页
      if (!isLoadMore) {
        this.currentPage = 1;
        this.ticketList = [];
        this.finished = false;
        this.$router.replace({ path: '/finished', query: { num: this.sidebarNum || 0, search: val } });
      }
      
      try {
        this.closeLoading = false
        this.loading = true;
        const { List, TotalRows } = await getOtobTicket({
          Type: this.Type,
          KeyWords: val,
          OrderStatus: 'finished',
          Size: this.pageSize,
          Start: this.currentPage,
        });
        
        if (this.currentPage === 1) {
          this.ticketList = List.map((item) => ({
            ...item,
            TodoDesc: this.parseJson(item.TodoDesc || '[]'),
          }));
        } else {
          this.ticketList = this.ticketList.concat(
            List.map((item) => ({
              ...item,
              TodoDesc: this.parseJson(item.TodoDesc || '[]'),
            }))
          );
        }
        
        this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
        this.total = TotalRows;
        // 判断是否还有更多数据
        this.finished = List.length < this.pageSize;
        
      } catch (e) {
        console.log(e);
        this.$toast(e);
      } finally {
        this.loading = false;
      }
    },
    
    onCancel() {
      this.searchValue = '';
      this.hasSearch = true;
      // 取消搜索时，清空搜索关键词，重新加载分类数据
      this.searchStr = '';
      sessionStorage.removeItem('doneKey');
      this.finished = false;
      this.currentPage = 1;
      this.ticketList = [];
      // 重新加载当前分类的数据（不带搜索关键词）
      this.getTicketByType(this.sidebarNum);
    },
    
    // 取消深度搜索
    cancelDeepSearch() {
      console.log('[DonePage] 用户取消深度搜索');
      
      // 设置取消标志
      this.deepSearchCancelled = true;
      this.isDeepSearching = false;
      
      // 关闭Dialog
      if (this.deepSearchToast) {
        this.deepSearchToast = null;
        Dialog.close();
      }
      
      // 通知语音助手组件取消API调用
      if (this.$refs.voiceAssistant) {
        this.$refs.voiceAssistant.cancelApiCall();
      }
      
      // 清空搜索关键词
      this.searchValue = '';
      this.searchStr = '';
      sessionStorage.removeItem('doneKey');
      
      // 重新加载已办列表（不带关键词）
      this.onSearch('');
      
      // 显示取消提示
      this.$toast({
        message: '已取消深度搜索',
        type: 'success',
        duration: 2000
      });
    },
    
    // 获取数据类型
    async getTaskType() {
      try {
        if (sessionStorage.getItem('doneTitleLists')) {
          const titleLists = JSON.parse(sessionStorage.getItem('doneTitleLists'));
          if (Array.isArray(titleLists)) {
            this.titleLists = titleLists;
            return;
          } else {
            this.titleLists = [];
          }
        }
      } catch(e) {}
      try {
        
        const res = await getAllType('finished');
        this.titleLists = res.map((item) => ({
          name: item.TypeName || item.Type,
          value: item.Type,
          count: item.amount,
        }));
        this.titleLists.unshift({
          name: '全部已办',
          value: '',
          count: this.titleLists.reduce((res, item) => parseInt(item.count) + res, 0),
        });
        sessionStorage.setItem('doneTitleLists', JSON.stringify(this.titleLists));
      } catch (e) {
        console.log(e);
      }
    },

    //点击品类进行过滤接口
    async getTicketByType(i, isLoadMore = false) {
      const { value } = this.titleLists?.[i || 0]|| {};
      
      // 如果不是加载更多，需要更新路由和清空搜索
      if (!isLoadMore) {
        this.$router.replace({ path: '/finished', query: { num: i } });
        this.activeName = '';
        
        // 清空搜索关键词和关闭搜索框
        this.searchValue = '';
        this.searchStr = '';
        this.hasSearch = true; // 关闭搜索框
        sessionStorage.removeItem('doneKey'); // 清除sessionStorage中的搜索关键词
        
        // 重置分页属性和列表数据
        this.currentPage = 1;
        this.finished = false;
        this.ticketList = [];
      }
      
      this.Type = value;
      this.loading = true;
      try {
        this.closeLoading = false

        const { List, TotalRows } = await getOtobTicket({
          Type: value,
          OrderStatus: 'finished',
          Size: this.pageSize,
          Start: this.currentPage,
        });
        if (this.currentPage === 1) {
          this.ticketList = List.map((item) => ({
            ...item,
            TodoDesc: this.parseJson(item.TodoDesc || '[]'),
          }));
        } else {
          this.ticketList = this.ticketList.concat(
            List.map((item) => ({
              ...item,
              TodoDesc: this.parseJson(item.TodoDesc || '[]'),
            }))
          );
        }
        this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
        this.total = TotalRows;
        this.titleLists[i || 0].count = TotalRows;
        this.finished = List.length < this.pageSize;
      } catch (e) {
        this.$toast(e);
        console.log(e)
      } finally {
        this.loading = false;
      }
    },

    parseJson(json) {
      try {
        return JSON.parse(json);
      } catch (e) {
        return [];
      }
    },

    // 工单详情页
    gotoDetail(item) {
      // 单独取出工单类型
      this.loading = true;
      
      
      if (!item.TaskId) {
        this.$router.push({
          path: '/fins-' + item.Type,
          query: { Id: item.TaskOrderId },
        });
      } else {
        this.$router.push({
          path: '/todo-detail',
          query: { ticketId: item.TicketId, taskId: item.TaskId, isHistory: '1', key: this.searchStr },
        });
      }
    },
    
    // 语音识别完成处理
    handleVoiceRecognition(recognitionText) {
      console.log('[DonePage] 语音识别完成:', recognitionText);
    },

    // 处理tickets响应（混元API返回的工单列表）
    async handleTicketResponse(data) {
      try {
        console.log('[DonePage] 处理tickets响应:', data);
        
        // 检查深度搜索是否已被取消
        if (this.deepSearchCancelled) {
          console.log('[DonePage] 深度搜索已被取消，忽略响应');
          return;
        }
        
        // 关闭深度搜索Dialog（如果存在）
        if (this.deepSearchToast) {
          this.deepSearchToast = null;
          Dialog.close();
        }
        this.isDeepSearching = false;
        
        const { tickets, params, query } = data;
        
        // 情况1: tickets为空
        if (!tickets || tickets.length === 0) {
          console.log('[DonePage] tickets为空，没有搜到已办');
          
          // 清空已办列表
          this.ticketList = [];
          this.total = 0;
          
          // 显示提示
          const message = params || '没有搜到匹配的已办';
          this.$toast({
            message,
            type: 'warning',
            duration: 3000
          });
          this.checkSearch()
          
          // 播放语音提示
          this.speakText(message);
          
          // 关闭AI对话框
          this.closeVoiceAssistant();
          return;
        }
        
        // 情况2: tickets只有一条数据，直接跳转
        if (tickets.length === 1) {
          console.log('[DonePage] tickets只有一条，直接跳转');
          
          const ticket = tickets[0];
          
          // 上报语音助手自动打开工单日志
          try {
            const { aegis } = this.$store.state.user;
            if (aegis) {
              aegis.infoAll({
                msg: JSON.stringify({
                  action: 'voice_auto_open_done',
                  source: 'voice_assistant',
                  taskId: ticket.TaskId || ticket.TaskOrderId,
                  ticketId: ticket.TicketId,
                  type: ticket.Type,
                  typeName: ticket.TypeName,
                  title: ticket.TodoName || ticket.TitleContent,
                  voiceQuery: query || this.lastUserVoiceQuery,
                  timestamp: new Date().toISOString()
                }),
                ext1: 'voice-auto-open-done',
                trace: 'voice-open-done',
              });
            }
          } catch (reportError) {
            console.error('[DonePage] 上报语音自动打开工单日志失败:', reportError);
          }
          
          // 显示提示
          const message = params || '已找到已办，即将自动打开...';
          this.$toast({
            message,
            type: 'success',
            duration: 500
          });
          
          // 0.5秒后自动跳转
          setTimeout(() => {
            this.gotoDetail(ticket);
            // 关闭AI对话框
            this.closeVoiceAssistant();
          }, 500);
          return;
        }
        
        // 情况3: tickets包含多条数据
        if (tickets.length > 1) {
          console.log(`[DonePage] tickets包含${tickets.length}条数据，展示列表`);
          
          // 需要通过API获取完整的已办信息来展示
          this.loading = true;
          
          try {
            // 提取所有的TaskId
            const taskIds = tickets.map(t => t.TaskId).filter(Boolean);
            
            if (taskIds.length > 0) {
              const List = this.promptList || [];
              
              // 过滤出匹配的已办
              const matchedTickets = List.filter(item => 
                taskIds.includes(item.TaskId) || taskIds.includes(item.TaskOrderId)
              );
              
              // 如果过滤后有结果，使用过滤结果；否则使用全部搜索结果
              const displayTickets = matchedTickets.length > 0 ? matchedTickets : List;
              
              // 展示已办列表
              this.ticketList = displayTickets.map((item) => ({
                ...item,
                TodoDesc: this.parseJson(item.TodoDesc || '[]'),
              }));
              this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
              this.total = this.ticketList.length;
              
              // 上报语音搜索返回多条结果日志
              try {
                const { aegis } = this.$store.state.user;
                if (aegis) {
                  aegis.infoAll({
                    msg: JSON.stringify({
                      action: 'voice_search_multiple_results',
                      source: 'voice_assistant',
                      voiceQuery: query || this.lastUserVoiceQuery,
                      resultCount: this.ticketList.length,
                      ticketsCount: tickets.length,
                      timestamp: new Date().toISOString(),
                      results: this.ticketList.slice(0, 5).map(item => ({
                        taskId: item.TaskId || item.TaskOrderId,
                        ticketId: item.TicketId,
                        type: item.Type,
                        typeName: item.TypeName,
                        title: item.TodoName || item.TitleContent
                      }))
                    }),
                    ext1: 'voice-search-multiple-done',
                    trace: 'voice-search-done',
                  });
                }
              } catch (reportError) {
                console.error('[DonePage] 上报语音搜索多条结果日志失败:', reportError);
              }
              
            } else {
              // 如果没有TaskId，直接展示tickets数据（可能需要格式转换）
              this.ticketList = tickets.map((item) => ({
                ...item,
                TodoDesc: this.parseJson(item.TodoDesc || '[]'),
              }));
              this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
              this.total = tickets.length;
              
              // 上报语音搜索返回多条结果日志
              try {
                const { aegis } = this.$store.state.user;
                if (aegis) {
                  aegis.infoAll({
                    msg: JSON.stringify({
                      action: 'voice_search_multiple_results',
                      source: 'voice_assistant',
                      voiceQuery: query || this.lastUserVoiceQuery,
                      resultCount: this.ticketList.length,
                      ticketsCount: tickets.length,
                      timestamp: new Date().toISOString(),
                      results: this.ticketList.slice(0, 5).map(item => ({
                        taskId: item.TaskId || item.TaskOrderId,
                        ticketId: item.TicketId,
                        type: item.Type,
                        typeName: item.TypeName,
                        title: item.TodoName || item.TitleContent
                      }))
                    }),
                    ext1: 'voice-search-multiple-done',
                    trace: 'voice-search-done',
                  });
                }
              } catch (reportError) {
                console.error('[DonePage] 上报语音搜索多条结果日志失败:', reportError);
              }
            }
            
            // 显示提示消息
            const message = params || `共为你找到${tickets.length}条可能的工单数据，请选择`;
            this.$toast({
              message,
              type: 'warning',
              duration: 3000
            });
            
            // 播放提示音
            this.playNotificationSound();
            
            // 播放语音提示
            this.speakText(message);
            
          } catch (error) {
            console.error('[DonePage] 获取已办详情失败:', error);
            
            // 显示错误提示
            this.$toast({
              message: '获取已办详情失败',
              type: 'fail',
              duration: 2000
            });
          } finally {
            this.loading = false;
          }
          
          // 关闭AI对话框
          this.closeVoiceAssistant();
          return;
        }
        
      } catch (error) {
        console.error('[DonePage] 处理tickets响应失败:', error);
        
        this.$toast({
          message: '处理响应失败',
          type: 'fail',
          duration: 2000
        });
        
        // 关闭AI对话框
        this.closeVoiceAssistant();
      }
        this.loading = false
    },

    // AI接口响应处理
    handleAIResponse(data) {
      console.log('[DonePage] AI接口响应:', data);
      
      // 保存用户的完整语音输入（用于后续可能的重新调用）
      if (data?.query) {
        this.lastUserVoiceQuery = data.query;
        console.log('[DonePage] 保存用户语音输入:', this.lastUserVoiceQuery);
      }
      
      try {
        // 解析AI响应数据
        let aiResult = data?.result;
        if (typeof aiResult === 'string') {
          aiResult = JSON.parse(aiResult);
        }
        
        // 进一步解析嵌套的JSON
        if (aiResult?.data?.data?.result) {
          const resultStr = aiResult.data.data.result;
          if (typeof resultStr === 'string') {
            aiResult = JSON.parse(resultStr);
          } else {
            aiResult = resultStr;
          }
        }
        
        console.log('[DonePage] 解析后的AI结果:', aiResult);
        
        // 优先检查是否包含tickets字段（新的响应格式）
        if (aiResult && aiResult.tickets && aiResult.tickets.length > 0) {
          // 处理tickets响应格式
          this.handleTicketResponse({
            tickets: aiResult.tickets,
            params: aiResult.params,
            query: data.query || this.lastUserVoiceQuery
          });
        } else if (aiResult && aiResult.tool && !aiResult.ask) {
          // 检查是否包含tool和params字段（原有的工具调用格式）
          this.executeAITool(aiResult.tool, aiResult.params);
        }
        
      } catch (error) {
        console.error('[DonePage] 解析AI响应失败:', error);
        this.$toast({
          message: 'AI响应解析失败',
          type: 'fail',
          duration: 2000
        });
      }
    },

    // 执行AI工具调用
    executeAITool(tool, params) {
      console.log(`[DonePage] 执行AI工具: ${tool}`, params);
      
      // 工具调用映射表
      const toolHandlers = {
        // 搜索工单
        search: this.handleSearchTool,
        // 打开已办详情
        openTicket: this.handleOpenTicketTool,
        // 刷新列表
        refresh: this.handleRefreshTool,
        // 关闭/隐藏语音助手
        closeAI: this.handleCloseTool,
      };
      
      // 获取对应的处理函数
      const handler = toolHandlers[tool];
      
      if (handler && typeof handler === 'function') {
        try {
          // 调用对应的处理函数
          handler.call(this, params);
          
          // openTicket 工具不自动关闭弹窗和显示提示
          if (tool !== 'openTicket') {
            // 关闭语音助手弹窗
            this.closeVoiceAssistant();
            
            // 显示执行成功消息
            const toolDisplayName = this.getToolDisplayName(tool);
            this.$toast({
              message: `已执行指令：${toolDisplayName}`,
              type: 'success',
              duration: 3000
            });
          }
          
        } catch (error) {
          console.error(`[DonePage] 执行工具 ${tool} 失败:`, error);
          
          // 关闭语音助手弹窗
          this.closeVoiceAssistant();
          
          // 显示执行失败消息
          const toolDisplayName = this.getToolDisplayName(tool);
          this.$toast({
            message: `执行指令失败：${toolDisplayName} - ${error.message || '未知错误'}`,
            type: 'fail',
            duration: 3000
          });
        }
      } else {
        console.warn(`[DonePage] 未知的工具类型: ${tool}`);
        
        // 关闭语音助手弹窗
        this.closeVoiceAssistant();
        
        // 显示不支持的工具类型错误提示
        this.$toast({
          message: `错误：不支持的指令类型 "${tool}"，请尝试其他指令`,
          type: 'fail',
          duration: 3000
        });
      }
    },

    // 关闭语音助手弹窗
    closeVoiceAssistant() {
      // 通过ref调用语音助手组件的关闭方法
      if (this.$refs.voiceAssistant) {
        this.$refs.voiceAssistant.closeVoiceModal();
      }
    },

    // 获取工具显示名称
    getToolDisplayName(tool) {
      const displayNames = {
        search: '搜索',
        openTicket: '打开已办',
        refresh: '刷新',
        closeAI: '关闭语音助手',
      };
      return displayNames[tool] || tool;
    },

    // 处理搜索工具
    async handleSearchTool(params) {
      const { keyword, keywords } = params;
      const searchKeyword = keyword || keywords;
      
      if (!searchKeyword) {
        throw new Error('搜索关键词不能为空');
      }

      console.log(`[DonePage] 执行搜索: ${searchKeyword}`);

      // 将关键字同步到搜索框并展示
      this.searchValue = searchKeyword;
      this.searchStr = searchKeyword;
      this.hasSearch = false; // 展示搜索框

      try {
        // 1. 执行关键词搜索
        this.loading = true;
        const { List, TotalRows } = await getOtobTicket({
          Type: '',
          KeyWords: searchKeyword,
          OrderStatus: 'finished',
        });

        console.log(`[DonePage] 搜索到 ${List.length} 条已办记录`);

        // 更新路由，保留搜索关键词
        this.$router.replace({ 
          path: '/finished', 
          query: { 
            num: 0, 
            search: searchKeyword 
          } 
        });

        // 2. 根据搜索结果数量执行不同操作
        if (List.length === 0) {
          // 没有搜到已办
          await this.handleNoTicketFound(searchKeyword, true);
        } else {
      sessionStorage.setItem('doneKey', searchKeyword)
          // 搜到已办，显示搜索结果
          this.ticketList = List.map((item) => ({
            ...item,
            TodoDesc: this.parseJson(item.TodoDesc || '[]'),
          }));
          this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
          this.closeLoading = true;

          // 显示成功提示
          this.$toast({
            message: `搜到 ${List.length} 条已办`,
            type: 'success',
            duration: 2000
          });

          // 关闭语音助手弹窗
          this.closeVoiceAssistant();
        }

      } catch (error) {
        console.error('[DonePage] 搜索已办失败:', error);
        this.$toast({
          message: '搜索已办失败，请重试',
          type: 'fail',
          duration: 2000
        });
        this.closeVoiceAssistant();
      } finally {
        this.loading = false;
      }
        this.loading = false;
        this.closeLoading = true
    },

    // 处理打开已办工具
    async handleOpenTicketTool(params) {
      const { keyword, keywords } = params;
      const searchKeyword = keyword || keywords;
      
      if (!searchKeyword) {
        throw new Error('搜索关键词不能为空');
      }

      console.log(`[DonePage] 执行打开已办: ${searchKeyword}`);

      // 将关键字同步到搜索框并展示
      this.searchValue = searchKeyword;
      this.searchStr = searchKeyword;
      this.hasSearch = false; // 展示搜索框

      try {
        // 1. 执行关键词搜索
        this.loading = true;
        const { List, TotalRows } = await getOtobTicket({
          Type: '',
          KeyWords: searchKeyword,
          OrderStatus: 'finished',
        });

        console.log(`[DonePage] 搜索到 ${List.length} 条已办记录`);

        // 2. 根据搜索结果数量执行不同操作
        if (List.length === 0) {
          // 情况3: 没有搜到已办
          await this.handleNoTicketFound(searchKeyword, true);
        } else if (List.length === 1) {
      sessionStorage.setItem('doneKey', searchKeyword)
          // 情况1: 仅搜到1条已办记录
          this.handleSingleTicketFound(List[0], searchKeyword);
        } else {
      sessionStorage.setItem('doneKey', searchKeyword)
          // 情况2: 搜到多条已办记录
          this.handleMultipleTicketsFound(List.length, searchKeyword);
        }

      } catch (error) {
        console.error('[DonePage] 打开已办失败:', error);
        this.$toast({
          message: '搜索已办失败，请重试',
          type: 'fail',
          duration: 2000
        });
        this.closeVoiceAssistant();
      } finally {
        this.loading = false;
      }
        this.loading = false;
        this.closeLoading = true
    },

    // 处理单条已办的情况
    handleSingleTicketFound(ticket, keyword) {
      console.log('[DonePage] 找到单条已办，0.5秒后自动跳转');
      
      // 显示搜索结果
      this.ticketList = [ticket].map((item) => ({
        ...item,
        TodoDesc: this.parseJson(item.TodoDesc || '[]'),
      }));
      this.activeName = this.ticketList.map((item) => item?.TaskId || item?.TaskOrderId);
      
      // 更新路由，保留搜索关键词
      if (keyword) {
        this.$router.replace({ path: '/finished', query: { num: this.sidebarNum || 0, search: keyword } });
      }
      
      // 显示提示
      this.$toast({
        message: '已找到已办，即将自动打开...',
        type: 'success',
        duration: 500
      });

      // 0.5秒后自动跳转
      setTimeout(() => {
        this.gotoDetail(ticket);
        this.closeVoiceAssistant();
      }, 500);
    },

    // 处理多条已办的情况
    handleMultipleTicketsFound(count, keyword) {
      console.log(`[DonePage] 找到 ${count} 条已办，需要用户手动选择`);
      
      // 显示搜索结果（关键字已在 handleOpenTicketTool 中同步到搜索框）
      // 重新执行搜索以显示完整的搜索结果列表
      this.checkSearch(keyword);
      
      // 显示提示消息
      const message = `搜到${count}条符合条件的已办，请手动选择需要打开的已办`;
      this.$toast({
        message,
        type: 'warning',
        duration: 3000
      });

      // 播放提示音
      this.playNotificationSound();

      // 播放语音提示
      this.speakText(message);

      // 关闭语音助手弹窗
      this.closeVoiceAssistant();
    },

    // 处理没有找到已办的情况
    async handleNoTicketFound(keyword, isFromKeywords = false) {
      console.log('[DonePage] 没有搜到匹配的已办', { keyword, isFromKeywords });
      
      // 更新路由，保留搜索关键词
      this.$router.replace({ 
        path: '/finished', 
        query: { 
          num: this.sidebarNum || 0, 
          search: keyword 
        } 
      });
      
      // 清空已办列表，显示空状态
      this.ticketList = [];
      this.total = 0;
      
      // 如果是通过keywords查询且结果为空，则再次调用混元API
      if (isFromKeywords) {
        console.log('[DonePage] 通过keywords查询结果为空，准备再次调用混元API');
        
        // 设置深度搜索状态
        this.isDeepSearching = true;
        this.deepSearchCancelled = false;
        
        // 显示提示消息，带取消按钮
        this.deepSearchToast = Dialog.alert({
          title: 'AI深度搜索',
          message: '正在进行深度搜索，请稍候...',
          showCancelButton: true,
          cancelButtonText: '取消搜索',
          confirmButtonText: '',
          showConfirmButton: false,
          closeOnClickOverlay: false,
          beforeClose: (action, done) => {
            if (action === 'cancel') {
              this.cancelDeepSearch();
              done();
            } else {
              done();
            }
          }
        });
        
        // 获取完整的已办列表（前100条）
        try {
          const { List } = await getOtobTicket({
            Type: this.Type,
            OrderStatus: 'finished',
            Size: 100,
            Start: 1
          });
          this.promptList = List;
          
          // 检查是否已被取消
          if (this.deepSearchCancelled) {
            console.log('[DonePage] 深度搜索已被用户取消');
            return;
          }
          
          console.log(`[DonePage] 获取到 ${List.length} 条已办记录，准备传递给混元API`);
          
          // 通过ref调用语音助手组件的方法，再次发起混元API调用
          if (this.$refs.voiceAssistant) {
            // 使用保存的用户完整语音输入
            const userQuery = this.lastUserVoiceQuery || keyword;
            console.log('[DonePage] 使用用户语音输入:', userQuery);
            
            // 准备已办列表信息
            const ticketListInfo = List.map(item => ({
              ...item,
              TaskId: item.TaskId || item.TaskOrderId,
              TodoName: item.TodoName || item.TitleContent,
              TypeName: item.TypeName || item.Type,
              IsUrgent: item.IsUrgent,
              TodoDesc: item.TodoDesc
            }));
            
            // 调用语音助手组件的方法，再次发起混元API调用
            await this.$refs.voiceAssistant.retryWithTicketList(userQuery, JSON.stringify(ticketListInfo));
            
            // 深度搜索完成，关闭Dialog
            if (this.deepSearchToast) {
              this.deepSearchToast = null;
              Dialog.close();
            }
            this.isDeepSearching = false;
          }
        } catch (error) {
          console.error('[DonePage] 获取已办列表失败:', error);
          
          // 关闭深度搜索Dialog
          if (this.deepSearchToast) {
            this.deepSearchToast = null;
            Dialog.close();
          }
          this.isDeepSearching = false;
          
          // 显示错误提示
          const errorMessage = `没有搜到匹配"${keyword}"的已办，请重新输入关键词`;
          this.$toast({
            message: errorMessage,
            type: 'warning',
            duration: 3000
          });
        this.checkSearch()

          
          // 播放语音提示
          this.speakText(errorMessage);
        }
      } else {
        // 普通情况：显示文字提示
        const message = `没有搜到匹配"${keyword}"的已办，请重新输入关键词`;
        this.$toast({
          message,
          type: 'warning',
          duration: 3000
        });
        this.checkSearch()

        // 播放语音提示
        this.speakText(message);
        this.closeLoading = true
        this.loading = false

        // 不关闭语音助手弹框，保持打开状态，用户可以继续使用
        console.log('[DonePage] 已显示文字和语音提示，语音助手弹框保持打开');
      }
    },

    // 播放提示音
    playNotificationSound() {
      try {
        // 创建音频上下文
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // 设置音调和音量
        oscillator.frequency.value = 800; // 频率 800Hz
        gainNode.gain.value = 0.3; // 音量 30%

        // 播放 0.2 秒
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);

        console.log('[DonePage] 播放提示音');
      } catch (error) {
        console.error('[DonePage] 播放提示音失败:', error);
      }
    },

    // 使用Web Speech API播放语音
    speakText(text) {
      try {
        // 检查浏览器是否支持Web Speech API
        if (!('speechSynthesis' in window)) {
          console.warn('[DonePage] 浏览器不支持Web Speech API');
          return;
        }

        // 停止当前正在播放的语音
        window.speechSynthesis.cancel();

        // 创建语音合成实例
        const utterance = new SpeechSynthesisUtterance(text);
        
        // 设置语音参数
        utterance.lang = 'zh-CN'; // 中文
        utterance.rate = 1.0; // 语速（0.1-10，默认1）
        utterance.pitch = 1.0; // 音调（0-2，默认1）
        utterance.volume = 1.0; // 音量（0-1，默认1）

        // 监听事件
        utterance.onstart = () => {
          console.log('[DonePage] 开始播放语音:', text);
        };

        utterance.onend = () => {
          console.log('[DonePage] 语音播放完成');
        };

        utterance.onerror = (event) => {
          console.error('[DonePage] 语音播放错误:', event.error);
        };

        // 播放语音
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('[DonePage] 播放语音失败:', error);
      }
    },

    // 处理刷新工具
    handleRefreshTool(params) {
      console.log(`[DonePage] 执行刷新`);
      this.onRefresh();
    },

    // 处理关闭语音助手工具
    handleCloseTool(params) {
      console.log('[DonePage] 执行关闭语音助手指令');
      
      // 显示提示消息
      this.$toast({
        message: '语音助手已关闭',
        type: 'success',
        duration: 2000
      });

      // 延迟执行，让用户看到提示
      setTimeout(() => {
        // 调用语音助手组件的确认隐藏方法
        if (this.$refs.voiceAssistant) {
          this.$refs.voiceAssistant.confirmHideVoiceButton();
        }
      }, 500);
    },

    // AI接口错误处理
    handleAIError(data) {
      console.log('[DonePage] AI接口错误:', data);
      
      // 检查深度搜索是否已被取消
      if (this.deepSearchCancelled) {
        console.log('[DonePage] 深度搜索已被取消，忽略错误');
        return;
      }
      
      // 关闭深度搜索Dialog（如果存在）
      if (this.deepSearchToast) {
        this.deepSearchToast = null;
        Dialog.close();
      }
      this.isDeepSearching = false;
      
      this.$toast({
        message: 'AI处理失败',
        type: 'fail',
        duration: 2000
      });
    },
    // 触底加载
    async onLoad() {
      if (this.debounceFlag || this.closeLoading) {
        this.refreshing = false;
        this.loading = false;
        this.finished = true;
        return;
      };
      this.debounceFlag = true;

      if (this.refreshing) {
        this.ticketList = [];
        this.currentPage = 1;
        this.refreshing = false;
      }
      
      // 判断是搜索模式还是普通模式
      if (this.searchStr) {
        // 搜索模式：加载更多搜索结果
        if (this.currentPage > 1 || this.ticketList.length > 0) {
          await this.onSearch(this.searchStr, true);
          this.currentPage++;
        }
      } else {
        // 普通模式：加载更多分类数据
        // 只有在非第一页时才调用getTicketByType（分页加载）
        // 第一页的数据由切换tab时的getTicketByType加载
        if (this.currentPage > 1 || this.ticketList.length > 0) {
          await this.getTicketByType(this.sidebarNum, true);
          this.currentPage++;
        }
      }
      
      this.debounceFlag = false;
    },
    async onRefresh() {
      // 清空列表数据
      this.finished = false;
      await this.onLoad();
    },
  },
};
</script>

<style lang="less" scoped>
// ===== 腾讯系蓝色主题变量 =====
@primary: #0052d9;
@primary-light: #366ef4;
@primary-lighter: #ecf2fe;
@primary-bg: #f2f7ff;
@bg-page: #f5f6f7;
@bg-white: #ffffff;
@text-primary: #181b22;
@text-regular: #4e5969;
@text-secondary: #86909c;
@text-placeholder: #c0c4cc;
@border-color: #e5e6eb;
@border-light: #f0f1f3;
@radius-sm: 6px;
@radius-md: 10px;
@radius-lg: 14px;
@shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.04);
@shadow-md: 0 2px 8px rgba(0, 0, 0, 0.06);
@danger: #d54941;
@warning: #e37318;
@nav-width: 27vw;
@header-height: 36px;

// ===== 全局容器 =====
.wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: @bg-page;
}

.todo-box {
  height: 100%;

  .content {
    display: flex;
    height: 100%;
    overflow: hidden;
  }
}

// ===== 左侧分类导航 =====
.nav {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: @nav-width;
  background: @bg-white;
  border-right: 1px solid @border-light;
  display: flex;
  flex-direction: column;
  z-index: 10;
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-fold {
    left: -@nav-width;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-header {
  height: @header-height;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid @border-light;
  flex-shrink: 0;
}

.nav-header-title {
  font-size: 13px;
  font-weight: 600;
  color: @primary;
  letter-spacing: 0.5px;
}

.nav-list {
  flex: 1;
  overflow-y: auto;
  padding: 2px 0;

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 6px 9px 10px;
  margin: 2px 4px;
  border-radius: @radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:active {
    background: @primary-lighter;
  }

  &--active {
    background: @primary-lighter;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 18px;
      border-radius: 0 3px 3px 0;
      background: @primary;
    }

    .nav-item__text {
      color: @primary;
      font-weight: 600;
    }

    .nav-item__badge {
      background: @primary;
      color: #fff;
    }
  }
}

.nav-item__text {
  font-size: 13px;
  color: @text-primary;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  // -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
}

.nav-item__badge {
  flex-shrink: 0;
  min-width: 12px;
  height: 16px;
  line-height: 16px;
  padding: 0 1px;
  border-radius: 7px;
  font-size: 10px;
  text-align: center;
  background: #ebedf0;
  color: @text-secondary;
  margin-left: 2px;
  font-weight: 500;
}

// ===== 右侧主内容区 =====
.list-box {
  margin-left: @nav-width;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
  transition: margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-stretch {
    margin-left: 0;

    .todo-list {
      width: 100vw;
    }
  }

  .van-pull-refresh {
    flex: 1 0 auto;
  }
}

// ===== 顶部标题栏 =====
.todo-header {
  position: fixed;
  top: 0;
  right: 0;
  width: calc(100vw - @nav-width);
  height: @header-height;
  display: flex;
  align-items: center;
  padding: 0 4px;
  background: @bg-white;
  z-index: 999;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid @border-light;

  &.is-stretch {
    width: 100vw;
  }
}

.todo-header__left {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 50%;

  &:active {
    background: @bg-page;
  }

  .toggle-icon {
    font-size: 16px;
    color: @text-regular;
  }
}

.todo-header__center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
}

.todo-header__title {
  font-size: 16px;
  font-weight: 600;
  color: @text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.todo-header__count {
  font-size: 12px;
  font-weight: 500;
  color: @primary;
  background: @primary-lighter;
  padding: 1px 8px;
  border-radius: 10px;
  flex-shrink: 0;
}

.todo-header__right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.search-toggle {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  color: @text-regular;

  &:active {
    background: @bg-page;
  }
}

// ===== 搜索面板 =====
.search-panel {
  position: fixed;
  z-index: 999;
  top: @header-height;
  right: 0;
  width: calc(100vw - @nav-width);
  background: @bg-white;
  border-bottom: 1px solid @border-light;
  box-shadow: @shadow-sm;

  &.is-stretch {
    width: 100vw;
  }
}

.search-panel__input {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;

  ::v-deep .scanner-field {
    flex: 1;
    height: 36px;
    line-height: 36px;
    background: @bg-page;
    border-radius: 18px;
    padding: 0 12px;
    font-size: 13px;
    margin-top: 0;

    .van-field__body {
      height: 36px;
    }

    .van-icon-scan {
      color: @primary;
      font-size: 22px;
    }
  }
}

.search-panel__cancel {
  flex-shrink: 0;
  font-size: 14px;
  color: @primary;
  cursor: pointer;
  padding: 0 4px;
  white-space: nowrap;

  &:active {
    opacity: 0.7;
  }
}

// ===== 辅助输入工具栏 =====
.search-toolbar {
  display: flex;
  align-items: center;
  padding: 4px 12px 8px;
  gap: 12px;
  border-top: 1px solid @border-light;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 16px;
  background: @bg-page;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: @text-regular;
  transition: background 0.2s;

  &:active {
    background: @border-light;
  }
}

.show-search-list {
  margin-top: 44px;

  &.with-toolbar {
    margin-top: 82px;
  }
}

// ===== 待办列表 =====
.todo-list {
  margin-top: @header-height;
  width: calc(100vw - @nav-width);
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: @bg-page;
  height: calc(100vh - @header-height);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

// ===== 空状态 =====
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0 40px;
  color: @text-placeholder;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}

// ===== 工单卡片列表 =====
.ticket-cards {
  padding: 4px 6px;
}

.ticket-card {
  background: @bg-white;
  border-radius: @radius-md;
  margin-bottom: 4px;
  padding: 8px 10px;
  box-shadow: @shadow-sm;
  transition: box-shadow 0.2s;
  border: 1px solid @border-light;

  &:active {
    box-shadow: @shadow-md;
  }
}

.ticket-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.ticket-card__tags {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.type-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  line-height: 18px;
  background: @primary-lighter;
  color: @primary;
}

.expand-icon {
  color: @text-placeholder;
  flex-shrink: 0;
  transition: transform 0.25s;
}

.ticket-card__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.ticket-card__title {
  font-size: 14px;
  font-weight: 600;
  color: @text-primary;
  line-height: 1.5;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}

// ===== 卡片展开详情 =====
.ticket-card__body {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed @border-color;
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail-row {
  display: flex;
  font-size: 12px;
  line-height: 1.6;
}

.detail-label {
  flex-shrink: 0;
  min-width: 56px;
  color: @text-secondary;
  font-weight: 500;
  text-align: right;
  padding-right: 8px;
}

.detail-value {
  flex: 1;
  color: @text-regular;
  word-break: break-all;
  white-space: pre-line;
}

.action-btn {
  background: @primary !important;
  color: #fff !important;
  border: none !important;
  font-size: 11px !important;
  padding: 0 10px !important;
  height: 22px !important;
  line-height: 22px !important;
  font-weight: 500;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0, 82, 217, 0.18);

  &:active {
    background: @primary-light !important;
  }
}

// ===== 过渡动画 =====
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 500px;
}

// ===== Vant 组件覆盖 =====
::v-deep .van-pull-refresh__track {
  min-height: calc(100vh - @header-height);
}

::v-deep .van-list__finished-text {
  font-size: 12px;
  color: @text-placeholder;
  padding: 16px 0;
}

::v-deep .van-loading {
  padding: 20px 0;
}
</style>
